<template>
  <div class='relative flex h-full'>

    <FlashMessage class='z-50'/>

    <div
      ref='navigationRef'
      class='relative flex w-max flex-row overflow-visible'
    >
      <NarrowSidebar
        :sidebar-navigation='sidebarNavigation'
        :expanded='expanded'
        @show-filter-menu='onShowFilterMenu($event)'
        @hide-filter-menu='onHideFilterMenu'
        @show-more='onShowMore($event)'
        @show-less='onShowLess'
        @toggle-expand='expanded = !expanded'
      />
      <ExtendedNarrowSidebar
        :items='extendedSidebarNavigation'
        :expanded='expanded'
        @show-filter-menu='onShowFilterMenu($event)'
        @hide-filter-menu='onHideFilterMenu'
        @hide-extended-sidebar='onShowLess'
      />
      <MobileMenu
        :sidebar-navigation='sidebarNavigation'
        :mobile-menu-open='mobileMenuOpen'
        @close-menu='mobileMenuOpen = false'
      />
      <div
        class='relative'
        :class='[ extendedSidebarNavigation.length > 0 ? "left-full" : "left-0" ]'
      >
        <FilterMenu
          :open='showFilters'
          :current-module='currentModule'
          @mouse-left='onHideFilterMenu'
        />
      </div>
    </div>

    <!-- Content area -->
    <div class='flex flex-1 flex-col overflow-hidden'>
      <header class='w-full'>
        <div class='relative flex shrink-0 border-b border-gray-200 bg-white py-2 shadow-sm'>
          <button
            type='button'
            class='border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500 md:hidden'
            @click='mobileMenuOpen = true'
          >
            <span class='sr-only'>Open sidebar</span>
            <MenuAlt2Icon
              class='h-6 w-6'
              aria-hidden='true'
            />
          </button>
          <div class='flex flex-1 px-4 sm:px-6'>
            <GlobalSearch/>
            <div class='ml-2 flex items-center space-x-4 sm:ml-6 sm:space-x-6'>
              <Menu
                as='div'
                class='relative shrink-0'
              >
                <div>
                  <MenuButton class='flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'>
                    <span class='sr-only'>Open user menu</span>
                    <img
                      v-if='profileImage'
                      :src='profileImage'
                      alt='profile image'
                    >
                    <div
                      v-else-if='userInitials'
                      class='flex h-9 w-9 items-center justify-center rounded-full bg-gray-500 text-sm text-white'
                    >{{ userInitials }}</div>
                  </MenuButton>
                </div>
                <transition
                  enter-active-class='transition ease-out duration-100'
                  enter-from-class='transform opacity-0 scale-95'
                  enter-to-class='transform opacity-100 scale-100'
                  leave-active-class='transition ease-in duration-75'
                  leave-from-class='transform opacity-100 scale-100'
                  leave-to-class='transform opacity-0 scale-95'
                >
                  <MenuItems class='absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'>
                    <MenuItem
                      v-for='item in userNavigation'
                      :key='item.name'
                      v-slot='{ active }'
                    >
                      <div>
                        <div v-if='item.enabled'>
                          <a
                            v-if='item.action'
                            :class='[active ? "bg-gray-100" : "", "block px-4 py-2 text-sm text-gray-700 cursor-pointer"]'
                            @click='item.action'
                          >
                            {{ item.name }}
                          </a>
                          <a
                            v-else
                            :href='item.href'
                            :class='[active ? "bg-gray-100" : "", "block px-4 py-2 text-sm text-gray-700"]'
                          >
                            {{ item.name }}
                          </a>
                        </div>
                      </div>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>
      </header>

      <!-- Main content -->
      <div class='flex flex-1 items-stretch overflow-hidden'>
        <main class='relative flex-1 overflow-y-auto'>
          <AppLoader/>

          <VerificationModal class='z-50'/>
          <ConfirmActionModal class='z-50'/>
          <TicketAccessModal
            ref='ticketAccessModal'
            :visible='ticketAccessModalVisible'
            class='z-50'
          />

          <!-- Primary column -->
          <section
            aria-labelledby='primary-heading'
            class='flex h-full min-w-0 flex-1 flex-col overflow-x-hidden lg:order-last'
          >
            <router-view :key='$router.name'/>
          </section>
        </main>

        <component
          :is='ticketsBar'
          v-if='ticketsBar'
        />

      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, defineAsyncComponent, ref, onMounted, computed, watch } from 'vue'
  import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
  import { MenuAlt2Icon,
           AcademicCapIcon,
           BellIcon } from '@heroicons/vue/outline'
  import NarrowSidebar from './partials/default/NarrowSidebar.vue'
  import ExtendedNarrowSidebar from './partials/default/ExtendedNarrowSidebar.vue'
  import MobileMenu from './partials/default/mobile-menu.vue'
  import { useAuthStore } from '@/store/authentication'
  import GlobalSearch from '@/layouts/partials/default/GlobalSearch.vue'
  import FilterMenu from '@/layouts/partials/default/FilterMenu.vue'
  import { useMenuStore } from '@/store/menu'
  import { useRouter } from 'vue-router'
  import FlashMessage from '@/components/FlashMessage.vue'
  import { AsideMenuItem } from '@/types/api/menu/menuTypes'
  import AppLoader from '@/components/AppLoader.vue'
  import VerificationModal from '@/components/actions/CustomActions/Global/Verification/VerificationModal.vue'
  import { onClickOutside } from '@vueuse/core'
  import { useUserStore } from '@/store/user'
  import { getUserInfo } from '@/api/requests/user/login'
  import { useModuleStore } from '@/store/module'

  const SHOW_TICKETS_BAR = import.meta.env.VITE_SHOW_TICKETS_BAR === 'true'

  export default defineComponent({
    // eslint-disable-next-line vue/match-component-file-name
    name: 'DefaultLayout',
    components: {
      FlashMessage,
      FilterMenu,
      GlobalSearch,
      Menu,
      MenuButton,
      MenuItem,
      MenuItems,
      MenuAlt2Icon,
      AcademicCapIcon,
      BellIcon,
      NarrowSidebar,
      ExtendedNarrowSidebar,
      MobileMenu,
      AppLoader,
      VerificationModal,
    },
    setup() {
      const authStore = useAuthStore()
      const isAuthenticated = computed(() => authStore.isAuthenticated())
      let menuLoaded = false
      const ticketAccessModalVisible = ref(false)

      const menuStore = useMenuStore()
      const userStore = useUserStore()
      const moduleStore = useModuleStore()
      const showFilters = ref(false)
      const sidebarNavigation = ref<Array<AsideMenuItem>>([])
      const extendedSidebarNavigation = ref({

      })

      const router = useRouter()
      const navigationRef = ref(null)

      onClickOutside(navigationRef, () => {
        onShowLess()
      })

      const expanded = ref(true)

      const logout = () => {
        router.push({
          name: 'logout',
        })
      }

      const navToProfile = () => {
        router.push({
          name: 'profile',
        })
      }

      const buildSidebarNavigation = () => {
        sidebarNavigation.value = []

        Object.entries(menuStore.menu ?? {
        }).forEach(([, value]) => {
          sidebarNavigation.value.push(value)
        })
      }

      const ensureMenuLoaded = async () => {
        if (!userStore?.user || menuLoaded) {
          return
        }

        if (userStore?.user?.force_ticket_selection && !authStore.getTicket()) {
          ticketAccessModalVisible.value = true

          return
        }

        try {
          if (!Object.keys(menuStore.menu ?? {
          }).length) {
            await menuStore.loadMenu()

            menuLoaded = true
          }

          buildSidebarNavigation()
        } catch (error: unknown) {
          type ErrorWithResponse = { response?: { status?: number } }
          const status = (error as ErrorWithResponse)?.response?.status

          if (status === 400 || status === 401) {
            authStore.logout()
            router.push('/login')
          }
        }
      }

      watch(() => isAuthenticated.value, (loggedIn) => {
        if (loggedIn) {
          ensureMenuLoaded()
        } else {
          sidebarNavigation.value = []
          router.push('/login')
        }
      }, {
        immediate: true,
      })

      watch(() => authStore.ticketId, async (newValue) => {
        if (newValue) {
          await ensureMenuLoaded()
        }
      })

      const profileImage = computed(() => {
        return userStore?.user?.profile_image || false
      })

      const userInitials = computed(() => {
        return (userStore?.user?.first_name ?? '').charAt(0) + (userStore?.user?.last_name ?? '').charAt(0)
      })

      onMounted(async () => {
        if (!userStore.user)  {
          await getUserInfo()
        }

        router.isReady().then(() => {
          ensureMenuLoaded()
        })
      })

      const toggleEditMode = () => {
        moduleStore.moduleEditMode = !moduleStore.moduleEditMode
      }

      const editModeAllowed = computed(() => {
        if (!userStore.user) return false

        return Object.values(userStore.user.edit_mode).includes(true)
      })

      const userNavigation = computed(() => [
        {
          name: 'Profile', action: navToProfile, enabled: true,
        },
        {
          name: 'Edit mode', action: toggleEditMode, enabled: editModeAllowed.value,
        },
        {
          name: 'Sign out', action: logout, enabled: true,
        },
      ])

      const mobileMenuOpen = ref(false)

      const currentModule = ref(Object())

      const onShowFilterMenu = (module: object) => {
        currentModule.value = module
        showFilters.value = true
      }

      const onHideFilterMenu = () => {
        showFilters.value = false
      }
      let ticketsBar = null

      const onShowMore = (items: object) => {
        if (JSON.stringify(extendedSidebarNavigation.value) === JSON.stringify(Object.values(items).filter(item => typeof(item) === 'object'))) {
          onShowLess()
        } else {
          extendedSidebarNavigation.value = Object.values(items).filter(item => typeof(item) === 'object')
        }
      }

      const onShowLess = () => {
        extendedSidebarNavigation.value = []
      }

      if (SHOW_TICKETS_BAR) {
        ticketsBar = defineAsyncComponent({
          loader: () => import('@/layouts/partials/default/TicketsBar.vue'),
        })
      }

      return {
        sidebarNavigation,
        userNavigation,
        mobileMenuOpen,
        showFilters,
        logout,
        ticketsBar,
        onShowFilterMenu,
        onHideFilterMenu,
        onShowMore,
        onShowLess,
        currentModule,
        extendedSidebarNavigation,
        showTickets: SHOW_TICKETS_BAR,
        navigationRef,
        profileImage,
        userInitials,
        navToProfile,
        expanded,
        ticketAccessModalVisible,
      }
    },
  })

</script>
